import { CheckIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import type { BillingInterval, BillingPlan } from './billing.interface'
import { BillingPlanCardButton } from './billing-plan-card-button.atom'

export interface BillingPlanCardProps {
  plan: BillingPlan
  isCurrentPlan?: boolean
  isCancelling?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  billingInterval?: BillingInterval
  onSelectPlan?: (plan: BillingPlan) => void
  className?: string
}

const formatPrice = (price: number): string => (price === 0 ? 'Free' : `$${price}`)

const calculateYearlySavings = (monthlyPrice: number, yearlyPrice: number): number => {
  if (monthlyPrice <= 0) return 0
  return Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)
}

interface PlanCardState {
  price: number
  periodLabel: string
  yearlySavings: number
  isFreePlan: boolean
  hideButton: boolean
}

const getPlanCardState = (
  plan: BillingPlan,
  billingInterval: BillingInterval,
  isCancelling: boolean
): PlanCardState => {
  const price = billingInterval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
  const periodLabel = billingInterval === 'monthly' ? '/mo' : '/yr'
  const yearlySavings =
    billingInterval === 'yearly' ? calculateYearlySavings(plan.monthlyPrice, plan.yearlyPrice) : 0
  const isFreePlan = plan.monthlyPrice === 0 && plan.yearlyPrice === 0
  const hideButton = isCancelling && isFreePlan
  return { price, periodLabel, yearlySavings, isFreePlan, hideButton }
}

const BillingPlanCardBase: React.FC<BillingPlanCardProps> = ({
  plan,
  isCurrentPlan = false,
  isCancelling = false,
  isLoading = false,
  isDisabled = false,
  billingInterval = 'monthly',
  onSelectPlan,
  className,
}): ReactNode => {
  const { price, periodLabel, yearlySavings, isFreePlan, hideButton } = getPlanCardState(
    plan,
    billingInterval,
    isCancelling
  )

  return (
    <Card
      className={cn(
        'billing-plan-card flex flex-col h-full',
        plan.isPopular && 'border-primary ring-1 ring-primary',
        className
      )}
    >
      <CardHeader className="pb-4">
        <PlanHeader planName={plan.name} isPopular={plan.isPopular} />
        <PlanPricing price={price} periodLabel={periodLabel} yearlySavings={yearlySavings} />
      </CardHeader>
      <CardContent className="flex-1">
        <PlanFeatureList featureList={plan.featureList} />
      </CardContent>
      <CardFooter className="pt-4">
        {hideButton ? (
          <div className="h-10" />
        ) : (
          <BillingPlanCardButton
            isCurrentPlan={isCurrentPlan}
            isCancelling={isCancelling}
            isLoading={isLoading}
            isDisabled={isDisabled}
            isFreePlan={isFreePlan}
            isPopular={plan.isPopular}
            onClick={() => onSelectPlan?.(plan)}
          />
        )}
      </CardFooter>
    </Card>
  )
}

const PlanHeader: React.FC<{ planName: string; isPopular?: boolean }> = ({
  planName,
  isPopular,
}) => (
  <div className="flex items-center justify-between">
    <CardTitle className="text-xl">{planName}</CardTitle>
    {isPopular && (
      <Badge variant="default" className="text-xs">
        Popular
      </Badge>
    )}
  </div>
)

const PlanPricing: React.FC<{ price: number; periodLabel: string; yearlySavings: number }> = ({
  price,
  periodLabel,
  yearlySavings,
}) => (
  <div className="mt-2">
    <span className="text-3xl font-bold">{formatPrice(price)}</span>
    {price > 0 && <span className="text-muted-foreground">{periodLabel}</span>}
    {yearlySavings > 0 && (
      <Badge variant="secondary" className="ml-2 text-xs">
        Save {yearlySavings}%
      </Badge>
    )}
  </div>
)

const PlanFeatureList: React.FC<{ featureList: string[] }> = ({ featureList }) => (
  <ul className="space-y-3">
    {featureList.map((feature) => (
      <li key={feature} className="flex items-start gap-2">
        <CheckIcon className="size-4 mt-0.5 text-primary shrink-0" />
        <span className="text-sm">{feature}</span>
      </li>
    ))}
  </ul>
)

export const BillingPlanCard = memo(BillingPlanCardBase)

BillingPlanCard.displayName = 'BillingPlanCard'
